import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { AnimatePresence, motion } from 'framer-motion';
import { LuArrowLeft, LuLoaderCircle, LuPencil, LuPlus, LuSave, LuTrash2, LuTriangleAlert } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import IconButton from '../../../components/ui/IconButton';
import Modal from '../../../components/ui/Modal';
import { GLASS_CHIP } from '../../../components/ui/glass';
import { useTranslation } from '../../../i18n/LanguageContext';

const FORM_ID = 'customer-record-form';
const FIRST_FIELD = 'input:not([disabled]), textarea:not([disabled])';

const ITEM_ACTION = 'tw-grid tw-h-8 tw-w-8 tw-place-items-center tw-rounded-full tw-bg-transparent tw-text-slate-400 tw-transition-colors focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50';

/**
 * A customer's addresses or contacts: the list, and in the same dialog the
 * add/edit form (Escape or ✕ there returns to the list, like the old
 * sub-dialog did). Deleting asks inline on the record itself.
 *  - renderItem(item): the record's content in the list
 *  - editing: null (list) or { mode: 'add' | 'edit' }; renderForm() draws the fields
 */
const RecordListModal = ({
  title,
  subtitle,
  icon: Icon,
  gradient,
  countLabel,
  addLabel,
  emptyLabel,
  emptyHint,
  deleteConfirmLabel,
  items,
  loading,
  renderItem,
  editing,
  formTitle,
  renderForm,
  formError,
  saving,
  onAdd,
  onEdit,
  onDelete,
  onCancelForm,
  onSubmit,
  onClose,
}) => {
  const { t, language } = useTranslation();
  const [confirmId, setConfirmId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const formRef = useRef(null);
  const formMode = editing?.mode;

  // Keyboard and mouse users start in the form's first field; touch screens
  // don't, as focusing would pop up the keyboard
  useEffect(() => {
    if (!formMode || !window.matchMedia('(pointer: fine)').matches) return undefined;
    const frame = requestAnimationFrame(() => formRef.current?.querySelector(FIRST_FIELD)?.focus({ preventScroll: true }));
    return () => cancelAnimationFrame(frame);
  }, [formMode]);

  const handleDelete = async (item) => {
    setDeletingId(item.id);
    try {
      await onDelete(item);
    } finally {
      setDeletingId(null);
      setConfirmId(null);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      event.currentTarget.requestSubmit();
    }
  };

  const icon = editing ? (
    <IconButton label={t('customers.common.back')} onClick={onCancelForm} disabled={saving}>
      <LuArrowLeft aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
    </IconButton>
  ) : (
    <GlassIcon gradient={gradient} size="xs">
      <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
    </GlassIcon>
  );

  const footer = editing ? (
    <>
      <Button variant="ghost" onClick={onCancelForm} disabled={saving}>{t('customers.common.cancel')}</Button>
      <Button variant="primary" type="submit" form={FORM_ID} disabled={saving} className="max-sm:tw-flex-1">
        {saving
          ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
          : <LuSave aria-hidden="true" className="tw-h-4 tw-w-4" />}
        {saving ? t('customers.common.saving') : t(editing.mode === 'edit' ? 'customers.common.update' : 'customers.common.save')}
      </Button>
    </>
  ) : null;

  const renderList = () => {
    if (loading && items.length === 0) {
      return (
        <ul aria-label={t('customers.common.loading')} className="tw-space-y-2.5">
          {[0, 1, 2].map((index) => (
            <li key={index} className={`tw-space-y-2 tw-rounded-2xl tw-p-4 ${GLASS_CHIP}`}>
              <span className="tw-block tw-h-3.5 tw-w-1/3 tw-animate-pulse tw-rounded-full tw-bg-slate-200/80 dark:tw-bg-white/10" />
              <span className="tw-block tw-h-3 tw-w-2/3 tw-animate-pulse tw-rounded-full tw-bg-slate-200/60 dark:tw-bg-white/[0.07]" />
            </li>
          ))}
        </ul>
      );
    }

    if (items.length === 0) {
      return (
        <div className="tw-flex tw-flex-col tw-items-center tw-gap-3 tw-px-6 tw-py-10 tw-text-center">
          <span className="tw-grid tw-h-14 tw-w-14 tw-place-items-center tw-rounded-2xl tw-bg-slate-100 tw-text-slate-400 dark:tw-bg-white/[0.05] dark:tw-text-slate-500">
            <Icon aria-hidden="true" className="tw-h-7 tw-w-7" />
          </span>
          <div className="tw-space-y-1">
            <p className="tw-text-sm tw-font-semibold tw-text-slate-700 dark:tw-text-slate-200">{emptyLabel}</p>
            <p className="tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{emptyHint}</p>
          </div>
          <Button variant="primary" size="sm" onClick={onAdd}>
            <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
            {addLabel}
          </Button>
        </div>
      );
    }

    return (
      <ul className="tw-space-y-2.5">
        <AnimatePresence initial={false}>
          {items.map((item) => {
            const confirming = confirmId === item.id;
            const deleting = deletingId === item.id;
            return (
              <motion.li
                key={item.id}
                layout
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.18 }}
                className={`tw-overflow-hidden tw-rounded-2xl ${GLASS_CHIP} ${confirming ? 'tw-ring-2 tw-ring-rose-500/30' : ''}`}
              >
                <div className="tw-flex tw-items-start tw-gap-3 tw-p-4">
                  <div className="tw-min-w-0 tw-flex-1">{renderItem(item)}</div>
                  {!confirming && (
                    <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-0.5">
                      <button
                        type="button"
                        onClick={() => onEdit(item)}
                        aria-label={t('customers.common.edit')}
                        title={t('customers.common.edit')}
                        className={`${ITEM_ACTION} hover:tw-bg-blue-500/10 hover:tw-text-blue-600 dark:hover:tw-text-blue-300`}
                      >
                        <LuPencil aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmId(item.id)}
                        aria-label={t('customers.common.delete')}
                        title={t('customers.common.delete')}
                        className={`${ITEM_ACTION} hover:tw-bg-rose-500/10 hover:tw-text-rose-600 dark:hover:tw-text-rose-300`}
                      >
                        <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                      </button>
                    </div>
                  )}
                </div>
                {confirming && (
                  <div role="alert" className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-border-t tw-border-rose-500/15 tw-bg-rose-500/[0.06] tw-px-4 tw-py-2.5">
                    <p className="tw-mr-auto tw-flex tw-items-center tw-gap-2 tw-text-sm tw-font-medium tw-text-rose-700 dark:tw-text-rose-300">
                      <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
                      {deleteConfirmLabel}
                    </p>
                    <Button size="sm" variant="ghost" onClick={() => setConfirmId(null)} disabled={deleting}>{t('customers.common.keep')}</Button>
                    <Button size="sm" variant="danger" onClick={() => handleDelete(item)} disabled={deleting}>
                      {deleting
                        ? <LuLoaderCircle aria-hidden="true" className="tw-h-4 tw-w-4 tw-animate-spin" />
                        : <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />}
                      {deleting ? t('customers.common.deleting') : t('customers.common.delete')}
                    </Button>
                  </div>
                )}
              </motion.li>
            );
          })}
        </AnimatePresence>
      </ul>
    );
  };

  return (
    <Modal
      isOpen
      onClose={() => {
        if (saving) return;
        if (editing) onCancelForm();
        else onClose();
      }}
      size="lg"
      title={editing ? formTitle : title}
      subtitle={editing ? `${title} · ${subtitle}` : subtitle}
      icon={icon}
      closeLabel={editing ? t('customers.common.back') : t('common.close')}
      lang={language}
      actions={!editing && items.length > 0 ? (
        <Button variant="primary" size="sm" onClick={onAdd} className="max-sm:tw-w-full">
          <LuPlus aria-hidden="true" className="tw-h-4 tw-w-4" />
          {addLabel}
        </Button>
      ) : null}
      footer={footer}
    >
      {editing ? (
        <form ref={formRef} id={FORM_ID} noValidate onSubmit={onSubmit} onKeyDown={handleKeyDown} className="tw-space-y-4">
          {formError && (
            <div role="alert" className="tw-flex tw-items-center tw-gap-2 tw-rounded-xl tw-bg-rose-500/10 tw-px-3.5 tw-py-2.5 tw-text-sm tw-font-medium tw-text-rose-700 dark:tw-text-rose-300">
              <LuTriangleAlert aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0" />
              {formError}
            </div>
          )}
          {renderForm()}
        </form>
      ) : (
        <div className="tw-space-y-3">
          {items.length > 0 && (
            <p className="tw-px-1 tw-text-xs tw-font-semibold tw-uppercase tw-tracking-[0.12em] tw-text-slate-400 dark:tw-text-slate-500">{countLabel}</p>
          )}
          {renderList()}
        </div>
      )}
    </Modal>
  );
};

RecordListModal.propTypes = {
  title: PropTypes.string.isRequired,
  subtitle: PropTypes.string,
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  countLabel: PropTypes.string.isRequired,
  addLabel: PropTypes.string.isRequired,
  emptyLabel: PropTypes.string.isRequired,
  emptyHint: PropTypes.string,
  deleteConfirmLabel: PropTypes.string.isRequired,
  items: PropTypes.arrayOf(PropTypes.object).isRequired,
  loading: PropTypes.bool,
  renderItem: PropTypes.func.isRequired,
  editing: PropTypes.shape({ mode: PropTypes.oneOf(['add', 'edit']).isRequired }),
  formTitle: PropTypes.string,
  renderForm: PropTypes.func.isRequired,
  formError: PropTypes.string,
  saving: PropTypes.bool,
  onAdd: PropTypes.func.isRequired,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onCancelForm: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default RecordListModal;
